import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Upload, Plus, Trash2, CheckCircle2, Loader2, ArrowRight, MessageCircle, CalendarCheck } from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALTRO = "ALTRO";
const WHATSAPP_URL = "https://wa.me/393345892285";
const BOOKING_URL = "https://cal.eu/robertabarbierato";

const inputCls =
  "bg-white border-[#C9A84C]/30 rounded-xl h-12 text-base focus-visible:border-[#C9A84C] focus-visible:ring-2 focus-visible:ring-[#C9A84C]/30 focus-visible:ring-offset-0";
const labelCls = "text-[11px] font-medium uppercase tracking-[0.14em] text-[#909090]";
const cardCls = "rounded-2xl border border-[#C9A84C]/25 bg-white p-6 shadow-[0_1px_3px_rgba(26,26,26,0.04)]";
const primaryBtn =
  "h-14 w-full rounded-full bg-[#1a1a1a] text-white text-base font-medium tracking-wide hover:bg-[#C9A84C] hover:text-[#1a1a1a] transition-colors disabled:opacity-40 disabled:hover:bg-[#1a1a1a] disabled:hover:text-white";

const newDoc = () => ({ id: Math.random().toString(36).slice(2), type: "", custom: "", file: null });

const Field = ({ label, id, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className={labelCls}>{label}</Label>
    {children}
  </div>
);

export default function ClientPortal() {
  const { t } = useT();
  const [docTypes, setDocTypes] = useState([]);
  const [config, setConfig] = useState({ privacy_url: "https://robertabarbierato.com/privacy" });
  const [cognome, setCognome] = useState("");
  const [nome, setNome] = useState("");
  const [telefono, setTelefono] = useState("");
  const [documents, setDocuments] = useState([newDoc()]);
  const [privacy, setPrivacy] = useState(false);
  const [website, setWebsite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const fileRefs = useRef({});

  useEffect(() => {
    axios.get(`${API}/document-types`).then((r) => setDocTypes(r.data)).catch(() => {});
    axios.get(`${API}/config`).then((r) => setConfig(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateDoc = (id, patch) => setDocuments((docs) => docs.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  const addDoc = () => setDocuments((docs) => [...docs, newDoc()]);
  const removeDoc = (id) => setDocuments((docs) => (docs.length > 1 ? docs.filter((d) => d.id !== id) : docs));

  const resolvedType = (d) => (d.type === ALTRO ? d.custom.trim() : d.type);
  const readyDocs = documents.filter((d) => d.file && resolvedType(d));
  const canSubmit =
    cognome.trim() && nome.trim() && telefono.trim() && privacy && readyDocs.length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("cognome", cognome.trim());
      fd.append("nome", nome.trim());
      fd.append("telefono", telefono.trim());
      fd.append("privacy_accepted", "true");
      fd.append("website", website);
      readyDocs.forEach((d) => {
        fd.append("doc_types", resolvedType(d));
        fd.append("files", d.file);
      });
      const r = await axios.post(`${API}/submit`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone({ ...r.data, documents: readyDocs.map((d) => resolvedType(d)) });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("doc.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f9f8f5]">
      <AppHeader />

      <main className="mx-auto max-w-md px-5 pb-28 pt-10">
        <AnimatePresence mode="wait">
          {done ? (
            <SuccessScreen key="done" done={done} onReset={() => window.location.reload()} t={t} />
          ) : (
            <motion.div key="form" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="space-y-7">
              <div className="text-center">
                <span className="text-[11px] font-medium uppercase tracking-[0.28em] text-[#C9A84C]">{t("doc.eyebrow")}</span>
                <h1 className="mt-3 font-serif text-[2.4rem] font-medium leading-tight text-[#1a1a1a] sm:text-5xl">{t("doc.title")}</h1>
                <p className="mx-auto mt-3 max-w-xs text-[15px] leading-relaxed text-[#4a4a4a]">{t("doc.subtitle")}</p>
              </div>

              <div className={`space-y-4 ${cardCls}`}>
                <Field label={t("doc.cognome")} id="cognome">
                  <Input id="cognome" data-testid="input-cognome" value={cognome} onChange={(e) => setCognome(e.target.value)} placeholder={t("doc.ph.cognome")} className={inputCls} />
                </Field>
                <Field label={t("doc.nome")} id="nome">
                  <Input id="nome" data-testid="input-nome" value={nome} onChange={(e) => setNome(e.target.value)} placeholder={t("doc.ph.nome")} className={inputCls} />
                </Field>
                <Field label={t("doc.telefono")} id="telefono">
                  <Input id="telefono" data-testid="input-telefono" type="tel" inputMode="tel" value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder={t("doc.ph.telefono")} className={inputCls} />
                </Field>
              </div>

              <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} className="hidden" aria-hidden="true" />

              <div className="space-y-4">
                <h2 className="text-center font-serif text-2xl font-medium text-[#1a1a1a]">{t("doc.documenti")}</h2>
                {documents.map((d, idx) => (
                  <DocumentCard key={d.id} index={idx} doc={d} docTypes={docTypes} canRemove={documents.length > 1}
                    onChange={(patch) => updateDoc(d.id, patch)} onRemove={() => removeDoc(d.id)}
                    fileRef={(el) => (fileRefs.current[d.id] = el)} t={t} />
                ))}
                <button type="button" data-testid="add-document-btn" onClick={addDoc}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#C9A84C]/40 bg-white py-3.5 text-sm font-medium text-[#1a1a1a] transition-colors hover:border-[#C9A84C] hover:text-[#C9A84C]">
                  <Plus className="h-4 w-4" /> {t("doc.aggiungi")}
                </button>
              </div>

              <div className={`flex items-start gap-3 ${cardCls}`}>
                <Checkbox id="privacy" data-testid="privacy-checkbox" checked={privacy} onCheckedChange={(v) => setPrivacy(!!v)}
                  className="mt-0.5 data-[state=checked]:bg-[#1a1a1a] data-[state=checked]:border-[#1a1a1a]" />
                <Label htmlFor="privacy" className="text-sm font-normal leading-relaxed text-[#4a4a4a]">
                  {t("doc.privacyPre")}
                  <a href={config.privacy_url} target="_blank" rel="noopener noreferrer" data-testid="privacy-link"
                    className="font-semibold text-[#1a1a1a] underline decoration-[#C9A84C] underline-offset-2">
                    {t("doc.privacyLink")}
                  </a>.
                </Label>
              </div>

              <div>
                <Button data-testid="submit-btn" disabled={!canSubmit} onClick={handleSubmit} className={primaryBtn}>
                  {submitting ? (
                    <span className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" /> {t("doc.invioInCorso")}</span>
                  ) : (
                    <span className="flex items-center gap-2">{t("doc.invia")} <ArrowRight className="h-5 w-5" /></span>
                  )}
                </Button>
                <p className="mt-3 text-center text-xs text-[#909090]">{t("doc.formati")}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

function DocumentCard({ index, doc, docTypes, canRemove, onChange, onRemove, fileRef, t }) {
  const inputId = `file-${doc.id}`;
  return (
    <div className={`space-y-3 ${cardCls}`} data-testid={`document-card-${index}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-[#C9A84C]">{t("doc.documento")} {index + 1}</span>
        {canRemove && (
          <button type="button" aria-label="Remove" data-testid={`remove-document-${index}`} onClick={onRemove} className="text-[#909090] transition-colors hover:text-red-500">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <Field label={t("doc.tipo")}>
        <Select value={doc.type} onValueChange={(v) => onChange({ type: v })}>
          <SelectTrigger data-testid={`doctype-select-${index}`} className={`${inputCls} data-[placeholder]:text-[#909090]`}>
            <SelectValue placeholder={t("doc.seleziona")} />
          </SelectTrigger>
          <SelectContent className="bg-white">
            {docTypes.map((it) => (<SelectItem key={it.id} value={it.label}>{it.label}</SelectItem>))}
            <SelectItem value={ALTRO}>ALTRO</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      {doc.type === ALTRO && (
        <Field label={t("doc.specifica")}>
          <Input data-testid={`doctype-custom-${index}`} value={doc.custom} onChange={(e) => onChange({ custom: e.target.value })} placeholder={t("doc.ph.specifica")} className={inputCls} />
        </Field>
      )}

      <input ref={fileRef} id={inputId} type="file" accept="application/pdf,image/jpeg,image/png,image/*" data-testid={`file-input-${index}`} className="hidden"
        onChange={(e) => onChange({ file: e.target.files?.[0] || null })} />
      <label htmlFor={inputId} data-testid={`carica-btn-${index}`}
        className="rb-dashed flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#C9A84C]/30 bg-[#f9f8f5] px-4 py-6 text-center">
        {doc.file ? (
          <>
            <CheckCircle2 className="h-6 w-6 text-emerald-500" />
            <span className="max-w-full truncate text-sm font-medium text-[#1a1a1a]">{doc.file.name}</span>
            <span className="text-xs text-[#909090]">{t("doc.tocca")}</span>
          </>
        ) : (
          <>
            <Upload className="h-6 w-6 text-[#C9A84C]" strokeWidth={1.5} />
            <span className="text-sm font-semibold text-[#1a1a1a]">{t("doc.carica")}</span>
            <span className="text-xs text-[#909090]">{t("doc.caricaHint")}</span>
          </>
        )}
      </label>
    </div>
  );
}

function SuccessScreen({ done, onReset, t }) {
  const count = done.files_count;
  return (
    <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
      className="flex flex-col items-center pt-8 text-center" data-testid="success-screen">
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50">
        <CheckCircle2 className="h-11 w-11 text-emerald-500" />
      </div>
      <h1 className="mt-6 font-serif text-[2.4rem] font-medium text-[#1a1a1a]">{t("doc.successTitle")}</h1>

      <div className={`mt-7 w-full text-left ${cardCls}`} data-testid="success-summary">
        <p className="text-base font-semibold text-[#1a1a1a]">{t("doc.docsSent", count)}</p>
        <ol className="mt-3 space-y-2">
          {done.documents.map((label, i) => (
            <li key={`${i}-${label}`} className="flex items-start gap-3 text-sm text-[#4a4a4a]">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#C9A84C]/15 text-[11px] font-semibold text-[#9a7d2e]">{i + 1}</span>
              <span className="font-medium">{label}</span>
            </li>
          ))}
        </ol>
        <p className="mt-5 border-t border-[#C9A84C]/15 pt-4 text-sm leading-relaxed text-[#4a4a4a]">{t("doc.notified")}</p>
      </div>

      <div className="mt-6 grid w-full grid-cols-1 gap-3">
        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="booking-btn"
          className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-[#1a1a1a] text-base font-medium text-white transition-colors hover:bg-[#C9A84C] hover:text-[#1a1a1a]">
          <CalendarCheck className="h-5 w-5" /> {t("doc.fissa")}
        </a>
        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-btn"
          className="flex h-14 w-full items-center justify-center gap-2 rounded-full border border-[#C9A84C]/40 bg-white text-base font-medium text-[#1a1a1a] transition-colors hover:border-[#C9A84C] hover:text-[#C9A84C]">
          <MessageCircle className="h-5 w-5" /> {t("doc.whatsapp")}
        </a>
      </div>

      <button data-testid="new-submission-btn" onClick={onReset} className="mt-6 text-sm font-medium text-[#909090] transition-colors hover:text-[#1a1a1a]">
        {t("doc.inviaAltri")}
      </button>
    </motion.div>
  );
}
